import { Alert, App, Button, Card, Form, Input, Result, Space, Typography } from 'antd';
import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { requestPasswordReset } from '../api/allauth';
import { ROUTES } from "../routes";
import styles from "./Accounts.module.css";

const { Title, Text, Paragraph } = Typography;

const RequestPasswordReset = () => {
    const [form] = Form.useForm();
    const [response, setResponse] = useState({ fetching: false, content: null });
    const { message } = App.useApp();

    const onFinish = async (values) => {
        setResponse({ ...response, fetching: true });

        try {
            const content = await requestPasswordReset(values.email);
            setResponse((r) => ({ ...r, content }));
        } catch (e) {
            console.error(e);
            message.error('Failed to send reset request. Please try again.');
        } finally {
            setResponse((r) => ({ ...r, fetching: false }));
        }
    };

    // Redirect if unauthorized
    if (response.content?.status === 401) {
        return <Navigate to="/account/password/reset/confirm" replace />;
    }

    // Show success state
    if (response.content?.status === 200) {
        return (
            <Card style={{ maxWidth: 500, width: '100%', textAlign: 'center' }}>
                <Result
                    status="success"
                    title="Reset Email Sent!"
                    subTitle="We've sent a password reset link to your email address. Please, check your inbox."
                    extra={[
                        <Link to={ROUTES.ACCOUNT.LOGIN}>Back to Login</Link>
                    ]}
                />
            </Card>
        );
    }

    // Error display component
    const FormErrors = ({ param, errors }) => {
        if (!errors?.length) return null;

        const errorMessages = errors
            .filter(error => !param || error.param === param)
            .map(error => error.message);

        if (!errorMessages.length) return null;

        return (
            <Alert
                message={errorMessages.join(', ')}
                type="error"
                showIcon
                style={{ marginTop: 8 }}
                closable
            />
        );
    };

    return (
        <div className={styles.card}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <img src="/w5n-logo.svg" alt="Logo" className={styles.logo} />
                <div style={{ textAlign: 'center' }}>
                    <Title level={2} style={{ "margin-top": 0 }}>Reset Password</Title>
                    <Paragraph type="secondary">
                        Enter your email address.<br /> We'll send you a link to reset your password.
                    </Paragraph>
                </div>

                {/* Global form errors */}
                <FormErrors errors={response.content?.errors} />

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    autoComplete="off"
                    disabled={response.fetching}
                >
                    <Form.Item
                        label="Email Address"
                        name="email"
                        rules={[
                            { required: true, message: 'Please input your email address!' },
                            { type: 'email', message: 'Please enter a valid email address!' }
                        ]}
                        validateStatus={response.content?.errors?.some(e => e.param === 'email') ? 'error' : ''}
                    >
                        <Input
                            size="large"
                            type="email"
                            placeholder="Enter your email address"
                            autoComplete="email"
                        />
                    </Form.Item>

                    {/* Email-specific errors */}
                    <FormErrors param="email" errors={response.content?.errors} />

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Button
                            htmlType="submit"
                            loading={response.fetching}
                            block
                            size="large"
                        >
                            Send Reset Link
                        </Button>
                    </Form.Item>
                </Form>

                <div style={{ textAlign: 'center' }}>
                    <Text type="secondary">
                        Remember your password?{' '}
                        <Link to={ROUTES.ACCOUNT.LOGIN}>
                            Back to login
                        </Link>
                    </Text>
                </div>
            </Space >
        </div>
    );
}

export default RequestPasswordReset;